import { createFileRoute, useSearch } from '@tanstack/react-router';
import { env } from '@/lib/env';
import { m } from '@/paraglide/messages';

type LoginErrorCode = 'not_invited' | 'domain_blocked' | 'oidc_error';

interface LoginSearch {
  error?: LoginErrorCode;
  return_to?: string;
}

const VALID_ERRORS: ReadonlySet<LoginErrorCode> = new Set([
  'not_invited',
  'domain_blocked',
  'oidc_error',
]);

function parseSearch(raw: Record<string, unknown>): LoginSearch {
  const out: LoginSearch = {};
  if (typeof raw.error === 'string' && VALID_ERRORS.has(raw.error as LoginErrorCode)) {
    out.error = raw.error as LoginErrorCode;
  }
  if (typeof raw.return_to === 'string' && raw.return_to.startsWith('/')) {
    out.return_to = raw.return_to;
  }
  return out;
}

export const Route = createFileRoute('/login')({
  validateSearch: parseSearch,
  component: LoginPage,
});

function LoginPage() {
  const { error, return_to } = useSearch({ from: Route.id });
  const returnTo = return_to ?? '/';
  const loginURL =
    `${env.apiOrigin}/auth/login?provider=google` +
    `&return_to=${encodeURIComponent(returnTo)}`;

  return (
    <main className="grid min-h-full place-items-center p-6">
      <section className="w-full max-w-md rounded-xl border border-border bg-card px-9 py-8 text-center shadow-sm">
        <p className="mb-5 text-xs uppercase tracking-wider text-muted">
          {m.app_name()}
        </p>
        <h1 className="mb-1 text-2xl font-semibold">{m.login_title()}</h1>
        <p className="mb-7 text-muted">{m.login_subtitle()}</p>

        {error && (
          <p
            role="alert"
            className="mb-5 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200"
          >
            {error === 'not_invited' && m.auth_error_not_invited()}
            {error === 'domain_blocked' && m.auth_error_domain_blocked()}
            {error === 'oidc_error' && m.auth_error_generic()}
          </p>
        )}

        <a
          href={loginURL}
          className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 font-medium text-primary-foreground transition hover:opacity-90"
        >
          {m.login_with_google()}
        </a>
      </section>
    </main>
  );
}
